import { Component, OnInit, OnDestroy, EventEmitter, Output } from '@angular/core';
import {
  StripeTerminal,
  Terminal,
  Reader,
  ConnectionStatus,
  PaymentStatus
} from '@stripe/terminal-js';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PaymentIntent } from '@stripe/stripe-js';
import { StoreService } from '../../services/store.service';
import { ToolsService } from '../../services/tools.service';
import { pages } from 'src/app/alv-enums';
import { StripePaymentService } from '../../services/stripe-payment.service';
declare var $: any;

/**
 * The Angular component for the Stripe Terminal integration.
 * It manages the entire client-side payment flow.
 */
@Component({
  selector: 'stripe-terminal',
  templateUrl: './stripe-payment-terminal.component.html',
  styleUrls: ['./stripe-payment-terminal.component.scss']
})
export class StripePaymentTerminalComponent implements OnInit, OnDestroy {
  // Public properties for the template to display status
  public statusMessage: string = 'Initializing Stripe Terminal...';
  public connectedReader: string | null = null;
  public discoveredReaders: Reader[] = [];
  public paymentStatus: PaymentStatus | string = 'NOT_STARTED';
  public paymentIntent: any = null;
  public amountToPay: number = this.storeService.shoppingCart.total; // Amount in cents ($10.00)
  public isLoading: boolean = false;
  public testCardNumber: string = '';

  // Private properties for internal component logic
  private terminal: Terminal | null = null;
  private readerDiscoverySubscription: Subscription | null = null;
  private ngUnsubscribe$ = new Subject<void>();
  private isSimulated: boolean = false;
  private isStripeTestMode: boolean = false;
  private currency: string | null = null;
  @Output() private paymentDone = new EventEmitter<pages>();
  @Output() private cardOptionChange = new EventEmitter<string>();

  // Use dependency injection to get the StripePaymentService
  constructor(
    private stripePaymentService: StripePaymentService,
    private storeService: StoreService,
    private toolsService: ToolsService
  ) { }

  /**
   * ngOnInit is an Angular lifecycle hook called after the component is initialized.
   */
  ngOnInit(): void {
    this.loadStripeTerminalSDK().then(() => {
      this.initializeStripe();
    }).catch(error => {
      console.error('Failed to load Stripe Terminal SDK:', error);
      this.statusMessage = 'Error: Failed to load SDK. Check console for details.';
    });
  }

  initializeStripe() {
    this.loadStripeTerminalSDK().then(async () => {
      this.initializeTerminal();
      await this.loadConfig();
    }).catch(error => {
      console.error('Failed to load Stripe Terminal SDK:', error);
      this.statusMessage = 'Error: Failed to load SDK. Check console for details.';
    });
  }

  async loadConfig() {
    try {
      const data = await this.stripePaymentService.getStripePaymentConfiguration().toPromise();
      this.currency = data.Currency;
      let publishableKey = data.PublishableKey;

      var setStripe = setInterval(function () {
        if (typeof (window as any).Stripe === "function") {
          (window as any).stripe = (window as any).Stripe(publishableKey);
          clearInterval(setStripe);
        }
      }, 300);
    } catch (error) {
      this.toolsService.showError('Failed to initialize Stripe: ' + error.message, 'error');
    }
  }

  /**
   * ngOnDestroy is a lifecycle hook called just before the component is destroyed.
   * This is crucial for cleaning up subscriptions and other resources to prevent memory leaks.
   */
  ngOnDestroy(): void {
    if (this.readerDiscoverySubscription) {
      this.readerDiscoverySubscription.unsubscribe();
    }
    this.terminal.disconnectReader().then(() => {
      console.log('Reader disconnected.');
    });
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  /**
   * Dynamically loads the Stripe Terminal JS SDK script.
   */
  private loadStripeTerminalSDK(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof (window as any).StripeTerminal !== 'undefined') {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/terminal/v1/';
      script.onload = () => resolve();
      script.onerror = (error) => reject(error);
      document.head.appendChild(script);
    });
  }

  private async onTerminalChange(): Promise<void> {
    await this.terminal.disconnectReader();
    this.setTerminalStatus(false);
    const terminalList = document.getElementById('terminal-list') as HTMLSelectElement;
    const reader = this.discoveredReaders.find(item => item.id === terminalList.value);
    if (reader != null) {
      localStorage.setItem('selectedReader', reader.id);
      // Manual selection - no auto-collect
      await this.connectReader(reader, false);
    } else {
      this.setTerminalStatus(false);
    }
  }

  /**
   * Initializes the Stripe Terminal instance.
   */
  private async initializeTerminal(): Promise<void> {
    if (typeof (window as any).StripeTerminal === 'undefined') {
      this.statusMessage = 'StripeTerminal not available.';
      return;
    }
    this.terminal = (window as any).StripeTerminal.create({
      onFetchConnectionToken: () => this.fetchConnectionToken(),
      onUnexpectedReaderDisconnect: () => this.handleDisconnect()
    });
    if (this.terminal) {
      await this.discoverReaders();
    }
    this.statusMessage = 'Stripe Terminal initialized. Ready for discovery.';
  }

  /**
   * Calls the service to get a connection token from your backend.
   */
  public async fetchConnectionToken(): Promise<string> {
    this.statusMessage = 'Requesting connection token from backend...';
    try {
      const response = await this.stripePaymentService.fetchConnectionToken().toPromise();
      this.statusMessage = 'Connection token received.';
      return response.secret;
    } catch (error) {
      console.error('Error fetching connection token:', error);
      this.statusMessage = 'Error fetching connection token. Check console.';
      throw error;
    }
  }

  /**
   * Starts the reader discovery process.
   */
  public async discoverReaders(): Promise<void> {
    const isSimulatedConfig = await this.getStripeTerminalConfig();
    const config = { simulated: isSimulatedConfig };
    this.isStripeTestMode = isSimulatedConfig;
    this.isSimulated = isSimulatedConfig;

    this.terminal.discoverReaders(config).then(async (discoverResult: { error: { message: string; }; discoveredReaders: Reader[]; }): Promise<void> => {
      if (discoverResult.error) {
        this.toolsService.showError('Failed to discover: ' + discoverResult.error.message, 'error');
      } else if (discoverResult.discoveredReaders.length === 0) {
        this.toolsService.showError('No available readers.', 'error');
      } else {
        this.discoveredReaders = discoverResult.discoveredReaders;
        if (this.discoveredReaders && this.discoveredReaders.length > 0) {
          this.populateTerminalList(this.discoveredReaders);
          // Auto-connect to first available reader (Kiosk should auto-connect)
          await this.tryAutoConnectToReader();
        }
      }
    });
  }

  /**
   * Auto-connects to a reader following priority order:
   * 1. Register-configured default reader (auto-collect enabled)
   * 2. localStorage saved reader (no auto-collect)
   * 3. First available reader (auto-collect for simulated only)
   */
  private async tryAutoConnectToReader(): Promise<void> {
    if (!this.discoveredReaders || this.discoveredReaders.length === 0) {
      return;
    }

    // Priority 1: Use configured default reader from Register Management
    const configuredDefaultReaderId = this.storeService.kioskSettings.defaultStripeReaderId;
    if (configuredDefaultReaderId) {
      const configuredReader = this.discoveredReaders.find(r => r.id === configuredDefaultReaderId);
      if (configuredReader) {
        const terminalList = document.getElementById('terminal-list') as HTMLSelectElement;
        if (terminalList) {
          terminalList.value = configuredDefaultReaderId;
        }
        // Auto-collect always enabled for configured default reader
        await this.connectReader(configuredReader, true);
        return;
      }
    }

    // Priority 2: Try localStorage saved reader (no auto-collect)
    const storedReaderId = localStorage.getItem('selectedReader');
    if (storedReaderId) {
      const storedReader = this.discoveredReaders.find(r => r.id === storedReaderId);
      if (storedReader) {
        const terminalList = document.getElementById('terminal-list') as HTMLSelectElement;
        if (terminalList) {
          terminalList.value = storedReaderId;
        }
        await this.connectReader(storedReader, false);
        return;
      }
    }

    // Priority 3: Auto-connect to first available reader (for Kiosk UX)
    const firstReader = this.discoveredReaders[0];
    if (firstReader) {
      const terminalList = document.getElementById('terminal-list') as HTMLSelectElement;
      if (terminalList) {
        terminalList.value = firstReader.id;
      }
      localStorage.setItem('selectedReader', firstReader.id);
      // Auto-collect only for simulated readers when no default configured
      await this.connectReader(firstReader, this.isSimulated);
    }
  }

  populateTerminalList(readers: Reader[]): void {
    const terminalList = document.getElementById('terminal-list');
    terminalList.innerHTML = '';
    const defaultOption = document.createElement('option');
    defaultOption.text = '--Select Terminal--';
    defaultOption.value = '';
    terminalList.appendChild(defaultOption);

    if (readers && readers.length > 0) {
      readers.forEach(item => {
        const option = document.createElement('option');
        option.text = item["label"];
        option.value = item["id"];
        terminalList.appendChild(option);
      });
    }
  }

  /**
   * Connects to a selected reader.
   * @param reader The reader to connect to
   * @param shouldAutoCollect Whether to auto-collect payment after connecting (for auto-connect scenarios)
   */
  public async connectReader(reader: Reader, shouldAutoCollect: boolean = false): Promise<void> {
    const terminalStatus = document.getElementById('terminal-status');
    if (terminalStatus) {
      terminalStatus.textContent = 'Connecting...';
      terminalStatus.classList.remove('connected', 'disconnected');
    }

    try {
      const connectResult = await this.terminal.connectReader(reader);
      if ('error' in connectResult && connectResult.error) {
        this.toolsService.showError('Failed to connect to reader.', 'error');
        this.setTerminalStatus(false);
      } else {
        const isConnected = this.terminal.getConnectionStatus() === 'connected';
        this.setTerminalStatus(isConnected, shouldAutoCollect && isConnected);
      }
    } catch (error) {
      console.error('Error connecting to reader:', error);
      this.toolsService.showError('Failed to connect to reader.', 'error');
      this.setTerminalStatus(false);
    }
  }

  public setTerminalStatus(isConnected: boolean, shouldAutoCollect: boolean = false) {
    const paymentMessage = document.getElementById('payment-message');
    const terminalStatus = document.getElementById('terminal-status');
    const collectButton = document.getElementById('collect-button') as HTMLButtonElement;
    const cancelButton = document.getElementById('cancel-button') as HTMLButtonElement;
    if (isConnected) {
      if (paymentMessage) paymentMessage.style.display = 'none';
      if (terminalStatus) {
        terminalStatus.textContent = '✅ Connected';
        terminalStatus.classList.remove('disconnected');
        terminalStatus.classList.add('connected');
      }
      if (collectButton) collectButton.disabled = false;
      if (cancelButton) cancelButton.disabled = false;

      // Auto-collect for simulated readers in Kiosk mode
      if (shouldAutoCollect && this.isSimulated && !this.isLoading) {
        this.prefillTestCardNumber();
        // Auto-trigger payment after a short delay (3 seconds for simulated readers)
        setTimeout(() => {
          if (this.terminal && this.terminal.getConnectionStatus() === 'connected' && !this.isLoading) {
            this.processPayment();
          }
        }, 3000);
      }
    } else {
      if (terminalStatus) {
        terminalStatus.textContent = '⚠ Not Connected';
        terminalStatus.classList.remove('connected');
        terminalStatus.classList.add('disconnected');
      }
      if (collectButton) collectButton.disabled = true;
      if (cancelButton) cancelButton.disabled = true;
    }
  }

  /**
   * Prefills the test card number field for simulated readers.
   */
  private prefillTestCardNumber(): void {
    if (this.isSimulated) {
      const testCardInput = document.getElementById('test-card-number') as HTMLInputElement;
      if (testCardInput && !testCardInput.value) {
        testCardInput.value = '4242424242424242';
        this.testCardNumber = '4242424242424242';
      }
    }
  }

  /**
   * Disconnects the currently connected reader.
   */
  public async disconnectReader(): Promise<void> {
    if (this.terminal) {
      await this.terminal.disconnectReader();
      this.connectedReader = null;
      this.statusMessage = 'Reader disconnected.';
      this.terminal.getConnectionStatus();
    }
  }

  /**
   * Handles the complete payment flow.
   */
  public async processPayment(): Promise<void> {
    if (!this.terminal || this.terminal.getConnectionStatus() !== 'connected') {
      this.statusMessage = 'Not connected to a reader. Please connect first.';
      return;
    }
    if (this.isSimulated && this.testCardNumber.length !== 16) {
      this.statusMessage = 'Invalid test card number.';
      return;
    }
    this.showLoader('Creating payment intent on backend...');

    try {
      // 1. Get a new PaymentIntent from backend
      this.showLoader("Processing payment...");
      let guestName = this.storeService.shoppingCart.guestName || '';
      let guestEmail = this.storeService.shoppingCart.guestEmail || '';
      let locationId = this.storeService.kioskSettings.locationId || 1;
      let customer = await this.stripePaymentService.getCustomer(guestName, guestEmail, locationId).toPromise();
      const paymentIntentResponse = await this.stripePaymentService.createPaymentIntent(this.amountToPay, this.currency, false, customer.customerId, locationId).toPromise();
      const clientSecret = paymentIntentResponse.clientSecret;
      this.statusMessage = 'Payment intent received. Ready to collect payment.';

      // 2. Set simulator config if needed
      if (this.isStripeTestMode) {
        this.terminal.setSimulatorConfiguration({ testCardNumber: $('#test-card-number').val() });
      }

      // 3. Collect a payment using the client secret
      this.showLoader("Capturing payment...");
      const result = await this.terminal.collectPaymentMethod(clientSecret);
      this.statusMessage = 'Card details collected. Confirming payment...';

      if ('error' in result && result.error) {
        throw new Error(result.error.message);
      }
      // 4. Process the payment with the reader
      if ('paymentIntent' in result && result.paymentIntent) {
        const confirmResult = await this.terminal.processPayment(result.paymentIntent);

        if ('error' in confirmResult && confirmResult.error) {
          throw new Error(confirmResult.error.message);
        }
      
        // 5. On success, process the payment on the backend
        if ('paymentIntent' in confirmResult && confirmResult.paymentIntent) {
          this.paymentIntent = confirmResult.paymentIntent;
          let paymentIntentId = result.paymentIntent.id;
          var captureResult = await this.stripePaymentService.capturePaymentIntent(paymentIntentId, this.amountToPay, this.currency).toPromise();
          await this.savePaymentResult(paymentIntentId);
        } else {
          throw new Error('PaymentIntent not found in confirmResult.');
        }
      } else {
        throw new Error('PaymentIntent not found in result.');
      }
    } catch (error) {
      console.error('Payment failed:', error);
      this.paymentStatus = 'FAILED';
      this.statusMessage = `Payment failed: ${error.message}`;
      this.hideLoader();
    }
  }

  async savePaymentResult(paymentIntentId: string) {
    try {
      await this.stripePaymentService.savePaymentResult(paymentIntentId, this.storeService.shoppingCart.total).toPromise();
      this.paymentStatus = 'SUCCEEDED';
      this.statusMessage = `Payment of $${this.amountToPay / 100} succeeded! Transaction saved.`;
      this.hideLoader();
      this.paymentDone.emit();
    } catch (error) {
      console.error('Error saving transaction:', error);
      this.statusMessage = `Error saving transaction: ${error.message}`;
      this.hideLoader();
      throw error;
    }
  }

  /**
   * Gets the Stripe test mode configuration from the backend.
   */
  private async getStripeTerminalConfig(): Promise<boolean> {
    try {
      const response = await this.stripePaymentService.getStripeTerminalConfig().toPromise();
      return response.IsStripeTestMode;
    } catch (error) {
      console.error('Failed to get terminal config:', error);
      return false; // Default to not simulated on error
    }
  }

  /**
   * Reconnects to the last known reader if it was unexpectedly disconnected.
   */
  public handleDisconnect(): void {
    this.connectedReader = null;
    this.statusMessage = 'Reader disconnected unexpectedly. Attempting to reconnect...';
    // Re-initialize to start the discovery process again
    this.initializeTerminal();
  }

  /**
   * Utility function to show a loading message.
   */
  public showLoader(message: string): void {
    this.isLoading = true;
    this.statusMessage = message;
  }

  /**
   * Utility function to hide the loader.
   */
  public hideLoader(): void {
    this.isLoading = false;
  }

  public cancelPayment() {
    this.terminal.disconnectReader();
    this.terminal.cancelCollectPaymentMethod();
    this.setTerminalStatus(false);
    const terminalList = document.getElementById('terminal-list') as HTMLSelectElement;
    terminalList.value = '';
    this.toolsService.showSuccess('Payment successfully cancelled.');
  }

  public onKeyIn() {
    this.cardOptionChange.emit('keyin');
  }
}